/**
 * C'est du Joly — Back in stock
 *
 * Loaded globally but inactive until a form carrying
 * [data-cdj-back-in-stock-form] is submitted. Cloudflare Turnstile is loaded
 * lazily and rendered explicitly because Gecko injects the form dynamically.
 */
(function () {
  'use strict';

  const FORM_SELECTOR = '[data-cdj-back-in-stock-form]';
  const BUTTON_SELECTOR = '[data-notify-stock-btn]';
  const TURNSTILE_SCRIPT_SELECTOR = 'script[data-cdj-turnstile-script]';
  const REQUEST_TIMEOUT_MS = 15000;
  const states = new WeakMap();
  let turnstilePromise = null;

  function getState(form) {
    let state = states.get(form);

    if (!state) {
      const submitButton = form.querySelector('[data-cdj-back-in-stock-submit]');
      const submitLabel = form.querySelector('[data-cdj-submit-label]');

      state = {
        widgetId: null,
        submitting: false,
        completed: false,
        submitButton,
        submitLabel,
        originalLabel: submitLabel ? submitLabel.textContent : '',
      };

      states.set(form, state);
    }

    return state;
  }

  function loadTurnstile() {
    if (window.turnstile && typeof window.turnstile.render === 'function') {
      return Promise.resolve(window.turnstile);
    }

    if (turnstilePromise) return turnstilePromise;

    turnstilePromise = new Promise(function (resolve, reject) {
      const callbackName = '__cdjBackInStockTurnstileReady';
      const existingScript = document.querySelector(TURNSTILE_SCRIPT_SELECTOR);

      window[callbackName] = function () {
        if (window.turnstile && typeof window.turnstile.render === 'function') {
          resolve(window.turnstile);
        } else {
          turnstilePromise = null;
          reject(new Error('Turnstile API unavailable.'));
        }
      };

      if (existingScript) {
        existingScript.addEventListener('load', window[callbackName], { once: true });
        existingScript.addEventListener('error', function () {
          existingScript.remove();
          turnstilePromise = null;
          reject(new Error('Unable to load Turnstile.'));
        }, { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=' +
        encodeURIComponent(callbackName) + '&render=explicit';
      script.async = true;
      script.defer = true;
      script.dataset.cdjTurnstileScript = 'true';
      script.addEventListener('error', function () {
        script.remove();
        turnstilePromise = null;
        reject(new Error('Unable to load Turnstile.'));
      }, { once: true });
      document.head.appendChild(script);
    });

    return turnstilePromise;
  }

  function setBusy(form, busy) {
    const state = getState(form);
    state.submitting = busy;

    if (state.submitButton) {
      state.submitButton.disabled = busy || state.completed;
      state.submitButton.setAttribute('aria-busy', busy ? 'true' : 'false');
    }

    if (state.submitLabel) {
      state.submitLabel.textContent = busy
        ? (form.dataset.submittingLabel || state.originalLabel)
        : state.originalLabel;
    }
  }

  function showMessage(form, type, message) {
    const element = form.querySelector('[data-cdj-back-in-stock-message]');
    if (!element) return;

    element.textContent = message || '';
    element.classList.remove('is-success', 'is-error', 'is-visible');

    if (message) {
      element.classList.add(type === 'success' ? 'is-success' : 'is-error', 'is-visible');
    }
  }

  function finishWithError(form, message) {
    const state = getState(form);
    if (state.completed) return;
    setBusy(form, false);
    showMessage(form, 'error', message || form.dataset.errorMessage);

    if (state.widgetId !== null && window.turnstile) {
      try {
        window.turnstile.reset(state.widgetId);
      } catch (error) {
        // The popup may have been removed while the request was running.
      }
    }
  }

  function finishWithSuccess(form) {
    const state = getState(form);
    state.completed = true;
    setBusy(form, false);
    showMessage(form, 'success', form.dataset.successMessage);

    const emailInput = form.querySelector('input[name="email"]');
    if (emailInput) emailInput.disabled = true;
    if (state.submitButton) state.submitButton.disabled = true;
  }

  function errorMessageForCode(form, code) {
    switch (code) {
      case 'invalid_email':
        return form.dataset.invalidEmailMessage;
      case 'security_verification_failed':
      case 'turnstile_failed':
        return form.dataset.securityErrorMessage;
      case 'configuration_error':
        return form.dataset.configurationErrorMessage;
      case 'variant_available':
        return form.dataset.alreadyAvailableMessage;
      default:
        return form.dataset.errorMessage;
    }
  }

  async function submitSubscription(form, token) {
    const endpoint = (form.dataset.endpoint || '').trim();
    const emailInput = form.querySelector('input[name="email"]');
    const honeypot = form.querySelector('input[name="website"]');

    if (!endpoint || !/^https:\/\//i.test(endpoint)) {
      finishWithError(form, form.dataset.configurationErrorMessage);
      return;
    }

    if (!emailInput || !emailInput.validity.valid) {
      if (emailInput) emailInput.reportValidity();
      finishWithError(form, form.dataset.invalidEmailMessage);
      return;
    }

    // Quietly accept obvious bot submissions without writing anything.
    if (honeypot && honeypot.value.trim() !== '') {
      finishWithSuccess(form);
      return;
    }

    const payload = {
      email: emailInput.value.trim(),
      product_id: form.dataset.productId,
      variant_id: form.dataset.variantId,
      locale: form.dataset.locale || document.documentElement.lang || 'fr',
      country_code: form.dataset.countryCode || '',
      product_url: form.dataset.productUrl || window.location.href,
      shop_domain: form.dataset.shopDomain || '',
      turnstile_token: token,
      website: '',
    };

    const controller = new AbortController();
    const timeout = window.setTimeout(function () {
      controller.abort();
    }, REQUEST_TIMEOUT_MS);

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        cache: 'no-store',
        redirect: 'error',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });

      let result = null;
      try {
        result = await response.json();
      } catch (error) {
        result = null;
      }

      if (!response.ok || !result || result.success !== true) {
        const code = result && typeof result.code === 'string' ? result.code : 'unknown_error';
        throw Object.assign(new Error(code), { code });
      }

      finishWithSuccess(form);
    } catch (error) {
      const code = error && error.code ? error.code : 'network_error';
      finishWithError(form, errorMessageForCode(form, code));
    } finally {
      window.clearTimeout(timeout);
    }
  }

  async function executeTurnstile(form) {
    const state = getState(form);
    const sitekey = (form.dataset.sitekey || '').trim();
    const container = form.querySelector('[data-cdj-turnstile]');

    if (!sitekey || !container) {
      finishWithError(form, form.dataset.configurationErrorMessage);
      return;
    }

    try {
      const turnstile = await loadTurnstile();

      if (state.widgetId === null) {
        state.widgetId = turnstile.render(container, {
          sitekey,
          action: 'back_in_stock',
          execution: 'execute',
          appearance: 'interaction-only',
          callback: function (token) {
            submitSubscription(form, token);
          },
          'error-callback': function () {
            finishWithError(form, form.dataset.securityErrorMessage);
            return true;
          },
          'expired-callback': function () {
            finishWithError(form, form.dataset.securityErrorMessage);
          },
          'timeout-callback': function () {
            finishWithError(form, form.dataset.securityErrorMessage);
          },
        });
      }

      turnstile.execute(state.widgetId);
    } catch (error) {
      finishWithError(form, form.dataset.securityErrorMessage);
    }
  }

  function getVariantIdFromButton(button) {
    const productForm = button.closest('form');
    const variantInput = productForm ? productForm.querySelector('input[name="id"]') : null;
    return (variantInput && variantInput.value) || button.dataset.variantId || '';
  }

  function buildSectionUrl(button) {
    const variantId = getVariantIdFromButton(button);
    if (!/^\d+$/.test(variantId)) return '';

    let rootUrl = button.dataset.rootUrl || '/';
    if (!rootUrl.endsWith('/')) rootUrl += '/';

    return rootUrl + 'variants/' + encodeURIComponent(variantId) + '/?section_id=back-in-stock';
  }

  function ensureFallbackDialogStyles() {
    if (document.getElementById('cdj-bis-dialog-styles')) return;

    const style = document.createElement('style');
    style.id = 'cdj-bis-dialog-styles';
    style.textContent = [
      '.cdj-bis-dialog{width:min(500px,calc(100vw - 30px));max-height:calc(100vh - 30px);padding:0;border:0;background:transparent;overflow:visible}',
      '.cdj-bis-dialog::backdrop{background:rgba(0,0,0,.55)}',
      '.cdj-bis-dialog__panel{position:relative;max-height:calc(100vh - 30px);overflow:auto;background:var(--t4s-body-background,#fff);box-shadow:0 10px 45px rgba(0,0,0,.25)}',
      '.cdj-bis-dialog__close{position:absolute;z-index:2;top:8px;right:8px;width:36px;height:36px;padding:0;border:0;border-radius:50%;background:#fff;color:#222;font-size:26px;line-height:34px;cursor:pointer;box-shadow:0 1px 8px rgba(0,0,0,.18)}'
    ].join('');
    document.head.appendChild(style);
  }

  function openFallbackDialog(html) {
    ensureFallbackDialogStyles();

    const dialog = document.createElement('dialog');
    dialog.className = 'cdj-bis-dialog';
    dialog.innerHTML = '<div class="cdj-bis-dialog__panel"><button type="button" class="cdj-bis-dialog__close" aria-label="Fermer">×</button>' + html + '</div>';

    const close = function () {
      try { dialog.close(); } catch (error) {}
      dialog.remove();
    };

    dialog.querySelector('.cdj-bis-dialog__close').addEventListener('click', close);
    dialog.addEventListener('cancel', function (event) {
      event.preventDefault();
      close();
    });
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) close();
    });

    document.body.appendChild(dialog);
    dialog.showModal();
  }

  function openFetchedPopup(html, button) {
    const cleanHtml = String(html || '').replace(/\[t4splitlz\]/g, '').trim();
    if (!cleanHtml) throw new Error('Empty back-in-stock section.');

    const jq = window.jQuery;
    if (jq && jq.magnificPopupT4s && typeof jq.magnificPopupT4s.open === 'function') {
      const content = jq('<div class="t4s-back-in-stock-popup-fallback"></div>').append(cleanHtml);
      jq.magnificPopupT4s.open({
        items: { src: content },
        type: 'inline',
        removalDelay: 500,
        tClose: (window.T4Sstrings && window.T4Sstrings.mfp_close) || 'Fermer',
        tLoading: (window.T4Sstrings && window.T4Sstrings.mfp_loading) || 'Chargement…',
        mainClass: 'mfp-move-horizontal t4s-inline-popup-wrapper t4s-rte ' + (button.dataset.id || ''),
        callbacks: {
          open: function () {
            jq(document).trigger('NTpopupInline:offClose');
            jq(document).trigger('currency:update');
          },
          afterClose: function () {
            jq(document).trigger('NTpopupInline:onClose');
          }
        }
      });
      return;
    }

    openFallbackDialog(cleanHtml);
  }

  async function openBackInStockFallback(button) {
    if (button.dataset.cdjBisLoading === 'true') return;

    const url = buildSectionUrl(button);
    if (!url) return;

    button.dataset.cdjBisLoading = 'true';
    button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'Accept': 'text/html' }
      });

      if (!response.ok) throw new Error('Unable to load back-in-stock section.');
      openFetchedPopup(await response.text(), button);
    } catch (error) {
      openFallbackDialog('<div style="padding:30px"><p>Impossible de charger le formulaire de notification. Veuillez réessayer.</p></div>');
    } finally {
      delete button.dataset.cdjBisLoading;
      button.removeAttribute('aria-busy');
    }
  }

  // Gecko normally binds Magnific Popup and populates data-mfp-src itself.
  // If that initialization did not run, take over the click without affecting
  // the normal native path when it is operational.
  document.addEventListener('click', function (event) {
    const button = event.target.closest(BUTTON_SELECTOR);
    if (!button) return;

    const nativeReady = button.classList.contains('t4s-mfp-enabled') &&
      Boolean((button.getAttribute('data-mfp-src') || '').trim());

    if (nativeReady) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    openBackInStockFallback(button);
  }, true);

  document.addEventListener('submit', function (event) {
    const form = event.target.closest(FORM_SELECTOR);
    if (!form) return;

    event.preventDefault();

    const state = getState(form);
    if (state.submitting || state.completed) return;

    const emailInput = form.querySelector('input[name="email"]');
    if (!emailInput || !emailInput.validity.valid) {
      if (emailInput) emailInput.reportValidity();
      showMessage(form, 'error', form.dataset.invalidEmailMessage);
      return;
    }

    showMessage(form, '', '');
    setBusy(form, true);
    executeTurnstile(form);
  });
})();
